import { useMemo, useState, type ReactNode } from 'react'
import type { EstoqueRow, StockStatus, AbcCurve } from '../../hooks/useEstoqueAnalytics'
import { useIsMobile } from '../../hooks/useIsMobile'

interface Props {
  rows: EstoqueRow[]
  extraFilter?: (row: EstoqueRow) => boolean
  extraFilterLabel?: string
  onClearExtraFilter?: () => void
  /** Quando presente, sobrescreve a ordenação interna (usado por cards-métrica). */
  sortOverride?: { key: SortKey; dir: 'asc' | 'desc' } | null
}

type SortKey = 'name' | 'current_stock' | 'monthly_turnover' | 'coverage_days' | 'revenue_window' | 'margin_pct' | 'value'

/** Valor em estoque do item (saldo × custo médio) — usado pela SortKey 'value'. */
function stockValue(r: EstoqueRow): number {
  return r.current_stock * (r.cost_avg ?? 0)
}

const STATUS_BADGE: Record<NonNullable<StockStatus>, string> = {
  ruptura: 'bg-red-900/40 text-red-300 border-red-700/40',
  excesso: 'bg-yellow-900/40 text-yellow-300 border-yellow-700/40',
  normal:  'bg-emerald-900/40 text-emerald-300 border-emerald-700/40',
}

const CURVE_BADGE: Record<NonNullable<AbcCurve>, string> = {
  A: 'bg-emerald-900/40 text-emerald-300',
  B: 'bg-sky-900/40 text-sky-300',
  C: 'bg-sunken text-ink-muted',
}

function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

function marginBadge(pct: number): string {
  if (pct >= 30) return 'bg-emerald-900/40 text-emerald-300 border-emerald-700/40'
  if (pct >= 15) return 'bg-yellow-900/40 text-yellow-300 border-yellow-700/40'
  return 'bg-red-900/40 text-red-300 border-red-700/40'
}


function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

export default function EstoqueTable({ rows, extraFilter, extraFilterLabel, onClearExtraFilter, sortOverride }: Props) {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'todos' | NonNullable<StockStatus>>('todos')
  const [curveFilter, setCurveFilter] = useState<'todos' | NonNullable<AbcCurve>>('todos')
  const [internalSortKey, setInternalSortKey] = useState<SortKey>('coverage_days')
  const [internalSortDir, setInternalSortDir] = useState<'asc' | 'desc'>('asc')
  const isMobile = useIsMobile()

  // Card-métrica (sortOverride) tem prioridade sobre a ordenação clicada na coluna.
  const sortKey = sortOverride?.key ?? internalSortKey
  const sortDir = sortOverride?.dir ?? internalSortDir

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    const sortVal = (r: EstoqueRow): number | string => {
      if (sortKey === 'value') return stockValue(r)
      const v = r[sortKey]
      return typeof v === 'number' ? v : v == null ? -Infinity : v.toString().toLowerCase()
    }
    return rows.filter(r => {
      if (extraFilter && !extraFilter(r)) return false
      if (statusFilter !== 'todos' && r.stock_status !== statusFilter) return false
      if (curveFilter !== 'todos' && r.abc_curve !== curveFilter) return false
      if (q && !r.name.toLowerCase().includes(q) && !r.sku.toLowerCase().includes(q)) return false
      return true
    }).sort((a, b) => {
      const an = sortVal(a)
      const bn = sortVal(b)
      if (an < bn) return sortDir === 'asc' ? -1 : 1
      if (an > bn) return sortDir === 'asc' ? 1 : -1
      return 0
    })
  }, [rows, search, statusFilter, curveFilter, sortKey, sortDir, extraFilter])

  function toggleSort(k: SortKey) {
    if (internalSortKey === k) setInternalSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setInternalSortKey(k); setInternalSortDir('asc') }
  }

  function arrow(k: SortKey): string {
    if (sortKey !== k) return ''
    return sortDir === 'asc' ? ' ▲' : ' ▼'
  }

  return (
    <div className="bg-surface border border-line rounded-xl overflow-hidden flex flex-col h-full">
      <div className="p-4 border-b border-line flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por SKU ou nome..."
          className="flex-1 min-w-[240px] bg-paper border border-line rounded px-3 py-1.5 text-ink text-sm placeholder:text-ink-faint"
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as typeof statusFilter)}
          className="bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm"
        >
          <option value="todos">Todos os status</option>
          <option value="ruptura">Em ruptura</option>
          <option value="normal">Normal</option>
          <option value="excesso">Em excesso</option>
        </select>
        <select
          value={curveFilter}
          onChange={e => setCurveFilter(e.target.value as typeof curveFilter)}
          className="bg-paper border border-line rounded px-2 py-1.5 text-ink text-sm"
        >
          <option value="todos">Todas as curvas</option>
          <option value="A">Curva A</option>
          <option value="B">Curva B</option>
          <option value="C">Curva C</option>
        </select>
        {extraFilter && extraFilterLabel && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-900/40 border border-yellow-700/40 text-yellow-200 text-xs">
            {extraFilterLabel}
            {onClearExtraFilter && (
              <button
                onClick={onClearExtraFilter}
                className="hover:text-white transition-colors"
                title="Limpar filtro"
              >
                ×
              </button>
            )}
          </span>
        )}
        <span className="text-xs text-ink-faint">{filtered.length} resultado(s)</span>
      </div>

      {isMobile ? (
        <div className="overflow-y-auto flex-1 min-h-0 p-2 space-y-2">
          {filtered.length === 0
            ? <p className="px-4 py-8 text-center text-ink-faint text-sm">Nenhum produto encontrado.</p>
            : filtered.map(r => <MobileEstoqueCard key={r.id} r={r} />)}
        </div>
      ) : (
      <div className="overflow-y-auto lg:overflow-auto flex-1 min-h-0">
        <table className="w-full text-sm mobile-stack-table">
          <thead className="bg-sunken border-b border-line sticky top-0">
            <tr>
              <th className="text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">SKU</th>
              <th onClick={() => toggleSort('name')}
                  className="cursor-pointer text-left text-xs font-semibold text-ink-muted uppercase px-4 py-2">
                Produto{arrow('name')}
              </th>
              <th onClick={() => toggleSort('current_stock')}
                  className="cursor-pointer text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">
                Saldo{arrow('current_stock')}
              </th>
              <th className="text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">Custo méd.</th>
              <th className="text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">Preço venda</th>
              <th onClick={() => toggleSort('monthly_turnover')}
                  className="cursor-pointer text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">
                Giro/mês{arrow('monthly_turnover')}
              </th>
              <th className="text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">Última venda</th>
              <th onClick={() => toggleSort('coverage_days')}
                  className="cursor-pointer text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">
                Cobertura{arrow('coverage_days')}
              </th>
              <th onClick={() => toggleSort('revenue_window')}
                  className="cursor-pointer text-right text-xs font-semibold text-ink-muted uppercase px-4 py-2">
                Receita{arrow('revenue_window')}
              </th>
              <th onClick={() => toggleSort('margin_pct')}
                  className="cursor-pointer text-center text-xs font-semibold text-ink-muted uppercase px-4 py-2">
                Margem %{arrow('margin_pct')}
              </th>
              <th className="text-center text-xs font-semibold text-ink-muted uppercase px-4 py-2">Curva</th>
              <th className="text-center text-xs font-semibold text-ink-muted uppercase px-4 py-2">Situação</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id} className="border-b border-line/60 hover:bg-sunken/30">
                <td className="px-4 py-2 font-mono text-xs text-ink-muted">{r.sku}</td>
                <td className="px-4 py-2 text-ink">{r.name}</td>
                <td className={`px-4 py-2 text-right tabular-nums ${r.current_stock <= 0 ? 'text-red-400 font-semibold' : 'text-ink'}`}>
                  {r.current_stock}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-muted">
                  {r.cost_avg != null ? brl(r.cost_avg) : '—'}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-muted">
                  {r.sale_price != null ? brl(r.sale_price) : '—'}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-muted">
                  {r.monthly_turnover != null ? r.monthly_turnover.toFixed(1) : '—'}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-muted">
                  {fmtDate(r.last_sale_at)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-muted">
                  {r.coverage_days != null ? `${r.coverage_days}d` : '—'}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-ink-muted">
                  {r.revenue_window != null ? brl(r.revenue_window) : '—'}
                </td>
                <td className="px-4 py-2 text-center">
                  {r.margin_pct != null ? (
                    <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium tabular-nums ${marginBadge(r.margin_pct)}`}>
                      {r.margin_pct.toFixed(1)}%
                    </span>
                  ) : <span className="text-ink-faint">—</span>}
                </td>
                <td className="px-4 py-2 text-center">
                  {r.abc_curve ? (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${CURVE_BADGE[r.abc_curve]}`}>{r.abc_curve}</span>
                  ) : <span className="text-ink-faint">—</span>}
                </td>
                <td className="px-4 py-2 text-center">
                  {r.stock_status ? (
                    <span className={`inline-block px-2 py-0.5 rounded border text-xs font-medium ${STATUS_BADGE[r.stock_status]}`}>
                      {r.stock_status}
                    </span>
                  ) : <span className="text-ink-faint">—</span>}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={12} className="px-4 py-8 text-center text-ink-faint">
                  Nenhum produto encontrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  )
}

function Metric({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wide text-ink-faint">{label}</p>
      <div className="text-[11px] text-ink font-medium tabular-nums leading-tight mt-0.5">{children}</div>
    </div>
  )
}

/** Card mobile de um SKU (substitui a linha da tabela no celular). */
function MobileEstoqueCard({ r }: { r: EstoqueRow }) {
  return (
    <div className="bg-surface border border-line rounded-xl px-3.5 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink leading-tight">{r.name}</p>
          <p className="text-[11px] text-ink-faint font-mono">{r.sku}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {r.stock_status && (
            <span className={`inline-block px-2 py-0.5 rounded border text-[10px] font-medium ${STATUS_BADGE[r.stock_status]}`}>{r.stock_status}</span>
          )}
          {r.abc_curve && (
            <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${CURVE_BADGE[r.abc_curve]}`}>Curva {r.abc_curve}</span>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-y-2 gap-x-2 mt-3">
        <Metric label="Saldo"><span className={r.current_stock <= 0 ? 'text-danger font-semibold' : ''}>{r.current_stock}</span></Metric>
        <Metric label="Cobertura">{r.coverage_days != null ? `${r.coverage_days}d` : '—'}</Metric>
        <Metric label="Giro/mês">{r.monthly_turnover != null ? r.monthly_turnover.toFixed(1) : '—'}</Metric>
        <Metric label="Receita">{r.revenue_window != null ? brl(r.revenue_window) : '—'}</Metric>
        <Metric label="Margem">
          {r.margin_pct != null
            ? <span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] ${marginBadge(r.margin_pct)}`}>{r.margin_pct.toFixed(1)}%</span>
            : '—'}
        </Metric>
        <Metric label="Últ. venda">{fmtDate(r.last_sale_at)}</Metric>
      </div>
    </div>
  )
}
